import * as React from 'react';
import { useLocation } from '@tanstack/react-router';
import {
  DashboardLayoutShell,
  type DashboardLayoutProps,
} from './DashboardLayout';
import {
  DashboardLayoutFrameContext,
  type DashboardLayoutConfig,
  type DashboardLayoutFrameContextValue,
} from './DashboardLayoutFrameContext';
import s from './DashboardLayout.module.scss';

function getInitialLayoutConfig(pathname: string): DashboardLayoutConfig {
  if (pathname.includes('/findings')) {
    return { activeNav: 'Findings', flush: true, hasAside: true };
  }
  if (pathname.includes('/governance')) {
    return { activeNav: 'Governance', flush: true, hasAside: false };
  }
  if (pathname.includes('/audit-log')) {
    return { activeNav: 'Settings', flush: true, hasAside: false };
  }
  if (pathname.includes('/mcp')) {
    return { activeNav: 'Developer Setup', flush: false, hasAside: false };
  }
  if (pathname.includes('/settings')) {
    return { activeNav: 'Settings', flush: true, hasAside: false };
  }
  return { activeNav: 'Dashboard', flush: false, hasAside: false };
}

export function DashboardLayoutFrame({
  children,
}: Pick<DashboardLayoutProps, 'children'>) {
  const { pathname } = useLocation();
  const [layoutConfig, setLayoutConfigState] =
    React.useState<DashboardLayoutConfig>(() =>
      getInitialLayoutConfig(pathname),
    );
  const [asideHost, setAsideHost] = React.useState<HTMLDivElement | null>(null);
  const [sidebarNavHost, setSidebarNavHost] =
    React.useState<HTMLDivElement | null>(null);

  const setLayoutConfig = React.useCallback((next: DashboardLayoutConfig) => {
    setLayoutConfigState((prev) => {
      if (
        prev.activeNav === next.activeNav &&
        prev.flush === next.flush &&
        prev.hasAside === next.hasAside &&
        prev.asideWidth === next.asideWidth &&
        prev.hasSidebarNav === next.hasSidebarNav
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const frameContext = React.useMemo<DashboardLayoutFrameContextValue>(
    () => ({ asideHost, sidebarNavHost, setLayoutConfig }),
    [asideHost, sidebarNavHost, setLayoutConfig],
  );

  const aside = layoutConfig.hasAside ? (
    <div ref={setAsideHost} className={s.asidePortalHost} />
  ) : null;
  const sidebarNav = layoutConfig.hasSidebarNav ? (
    <div ref={setSidebarNavHost} className={s.asidePortalHost} />
  ) : null;

  return (
    <DashboardLayoutFrameContext.Provider value={frameContext}>
      <DashboardLayoutShell
        activeNav={layoutConfig.activeNav}
        flush={layoutConfig.flush}
        aside={aside}
        asideWidth={layoutConfig.asideWidth}
        sidebarNav={sidebarNav}
      >
        {children}
      </DashboardLayoutShell>
    </DashboardLayoutFrameContext.Provider>
  );
}
